// ----------------------------------------------------------------------------
// automation — Curve editor and automation clip system for DAWSEUR
//
// Features:
//   - Akima spline interpolation (no overshoot, passes through all knots)
//   - Automation clips stored per-track per-parameter
//   - Real-time parameter application during playback
//   - Clips are like audio clips but store curve data instead of audio
// ----------------------------------------------------------------------------

const { createInterpolator, sampleCurve } = require('./akima.js');

// Global registry of automation clips
// Key: trackId:paramName  ->  { trackId, paramName, inputEl, min, max, step, points, lengthBeats, startBeat }
const automationClips = new Map();

// Active automation listeners (for playback)
// Key: trackId:paramName  ->  callback function
const automationListeners = new Map();

// Cache of interpolator functions per clip (rebuilt when points change)
const interpolatorCache = new Map();

function formatVal(v, min, max) {
   if (min < 0) return v.toFixed(2);
   if (max <= 1) return Math.round(v * 100) + '%';
   return v.toFixed(1);
}

// ---- Get or create interpolator for a clip ----
function getInterpolator(clip) {
   if (!clip || !clip.points || clip.points.length < 2) return null;
   const key = clip.trackId + ':' + clip.paramName;
   // Check cache — rebuild if point count or positions changed
   const cached = interpolatorCache.get(key);
   const sig = clip.points.length + ':' + clip.points.map(p => p.x.toFixed(4) + ',' + p.y.toFixed(4)).join('|');
   if (cached && cached.sig === sig) return cached.fn;
   // Build new interpolator
   const sortedPts = clip.points.slice().sort((a, b) => a.x - b.x);
   const fn = createInterpolator(sortedPts);
   interpolatorCache.set(key, { fn, sig });
   return fn;
}

// ---- Invalidate interpolator cache for a clip ----
function invalidateInterpolator(clip) {
   if (!clip) return;
   const key = clip.trackId + ':' + clip.paramName;
   interpolatorCache.delete(key);
}

// ---- Evaluate the full automation curve at a normalized position [0,1] ----
// Returns the parameter value (not normalized)
function evalAutomation(clip, normPos) {
   if (!clip || !clip.points || clip.points.length === 0) return null;
   const pts = clip.points;
   if (pts.length === 1) return pts[0].y;

   // Clamp position to knot range
   if (normPos <= pts[0].x) return pts[0].y;
   if (normPos >= pts[pts.length - 1].x) return pts[pts.length - 1].y;

   const interp = getInterpolator(clip);
   if (!interp) {
      // Fallback: linear interpolation
      for (let i = 0; i < pts.length - 1; i++) {
         if (normPos >= pts[i].x && normPos <= pts[i + 1].x) {
            const t = (normPos - pts[i].x) / (pts[i + 1].x - pts[i].x);
            return pts[i].y + (pts[i + 1].y - pts[i].y) * t;
         }
      }
      return pts[pts.length - 1].y;
   }

   const val = interp(normPos);
   // Clamp to [min, max] as safety
   const min = clip.min !== undefined ? clip.min : 0;
   const max = clip.max !== undefined ? clip.max : 1;
   return Math.max(min, Math.min(max, val));
}

// ---- Get automation value at a specific beat position ----
// External autoTracks source (set by dawseur.js to enable multi-clip playback)
let _externalAutoTracks = null;
function setExternalAutoTracks(tracks) {
   _externalAutoTracks = tracks;
}

function getAutomationValueAt(trackId, paramName, beat) {
   const clipKey = trackId + ':' + paramName;
   const clip = automationClips.get(clipKey);
   // Check the Map clip first
   if (clip) {
      const normPos = (beat - (clip.startBeat || 0)) / (clip.lengthBeats || 4);
      if (normPos >= 0 && normPos <= 1) {
         return evalAutomation(clip, normPos);
      }
   }
   // Check external (timeline) autoTracks for additional segments
   if (_externalAutoTracks) {
      for (const a of _externalAutoTracks) {
         if (String(a.parentId) !== String(trackId)) continue;
         if (a.paramName !== paramName) continue;
         const aStart = a.startBeat || 0;
         const aLen = a.lengthBeats || 4;
         const normPos = (beat - aStart) / aLen;
         if (normPos >= 0 && normPos <= 1) {
            return evalAutomation(a, normPos);
         }
      }
   }
   return null;
}

// ---- Register a listener for automation updates ----
function onAutomationUpdate(trackId, paramName, callback) {
   const clipKey = trackId + ':' + paramName;
   automationListeners.set(clipKey, callback);
}

// ---- Remove a listener ----
function offAutomationUpdate(trackId, paramName) {
   const clipKey = trackId + ':' + paramName;
   automationListeners.delete(clipKey);
}

// ---- Tick: called during playback to apply automation values ----
// engineRef: optional engine reference for direct param setting
function tickAutomations(currentBeat, engineRef) {
   // Build a unique set of trackId:paramName keys from both the Map and external autoTracks
   const seenKeys = new Set();
   for (const [key] of automationClips) seenKeys.add(key);
   if (_externalAutoTracks) {
      for (const a of _externalAutoTracks) {
         seenKeys.add(a.parentId + ':' + a.paramName);
      }
   }

   for (const clipKey of seenKeys) {
      const [trackId, paramName] = clipKey.split(':');
      // Find the active clip at the current beat (checks Map + external)
      const val = getAutomationValueAt(trackId, paramName, currentBeat);
      if (val === null) continue;

      // Find the Map clip for metadata (isKnob, knobId, inputEl, etc.)
      const mapClip = automationClips.get(clipKey);

      // 1. Call registered listener if any
      const listener = automationListeners.get(clipKey);
      if (listener) {
         try { listener(val); } catch (e) {}
      }

      // 2. Set the control value
      if (mapClip && mapClip.isKnob) {
         // Standalone knob automation — look up the knob in the registry
         let setter = mapClip.setValue;
         if (mapClip.knobId && typeof window !== 'undefined' && window._knobRegistry) {
            const entry = window._knobRegistry.get(mapClip.knobId);
            if (entry) setter = entry.setValueVisual;
         }
         if (setter) {
            try { setter(val); } catch (e) {}
         }
      } else if (mapClip && mapClip.inputEl) {
         try {
            if (mapClip.inputEl.setValue) {
               mapClip.inputEl.setValue(val);
            } else {
               mapClip.inputEl.value = val;
               mapClip.inputEl.dispatchEvent(new Event('change', { bubbles: true }));
            }
         } catch (e) {}
      }

      // 3. Handle track-level params via engine
      if (engineRef) {
         const pn = paramName.toLowerCase();
         const tid = trackId;
         try {
            if (pn === 'volume' || pn === 'vol') {
               engineRef.setTrackVolume(tid, val);
            } else if (pn === 'pan') {
               engineRef.setTrackPan(tid, val);
            } else if (pn === 'mixer volume' || pn === 'mixer vol' || pn === 'mixervolume') {
               engineRef.setTrackMixerVolume(tid, val);
            } else if (pn === 'mixer pan' || pn === 'mixerpan') {
               engineRef.setTrackMixerPan(tid, val);
            } else if (pn === 'eq low' || pn === 'eq lo' || pn === 'eq-low') {
               engineRef.setTrackEq(tid, 'low', val);
            } else if (pn === 'eq mid') {
               engineRef.setTrackEq(tid, 'mid', val);
            } else if (pn === 'eq high' || pn === 'eq hi' || pn === 'eq-high') {
               engineRef.setTrackEq(tid, 'high', val);
            } else if (pn === 'master volume' || pn === 'master vol') {
               engineRef.setMasterVolume(val);
            } else if (pn === 'master pan') {
               engineRef.setMasterPan(val);
            } else if (pn === 'master eq low') {
               engineRef.setMasterEq('low', val);
            } else if (pn === 'master eq mid') {
               engineRef.setMasterEq('mid', val);
            } else if (pn === 'master eq high') {
               engineRef.setMasterEq('high', val);
            }
         } catch (e) {}
      }
   }
}

// ---- Initialize: set up window.createAutomation for faderH context menu ----
// opts: { getTrackId, onClipCreated, onClipEdited }
function initAutomation(opts) {
   const getTrackId = opts.getTrackId;
   const onClipCreated = opts.onClipCreated || (() => {});
   const onClipEdited = opts.onClipEdited || (() => {});

   window.createAutomation = function(inputEl, bankId) {
      // Support both DOM input elements and knob-style objects
      // Knob objects: { label, min, max, step, getValue, setValue, element, standalone }
      const isKnobObj = inputEl && typeof inputEl === 'object' && !inputEl.dataset && inputEl.label !== undefined;
      const paramName = isKnobObj
         ? (inputEl.label || 'param')
         : (inputEl.dataset.label || inputEl.id || 'param');
      // For standalone knob automation, create a synthetic track ID
      const trackId = isKnobObj
         ? (inputEl.trackId || ('knob_' + paramName.replace(/\s+/g, '_')))
         : (inputEl.dataset.trackId || getTrackId() || 'Track 1');
      const min = isKnobObj ? Number(inputEl.min) : Number(inputEl.min);
      const max = isKnobObj ? Number(inputEl.max) : Number(inputEl.max);
      const step = isKnobObj ? (Number(inputEl.step) || 0.01) : (Number(inputEl.step) || 0.01);
      const currentVal = isKnobObj ? inputEl.getValue() : parseFloat(inputEl.value);

      const clipKey = trackId + ':' + paramName;
      const existing = automationClips.get(clipKey);

      if (existing) {
         // Already exists — just select it (switch to auto panel)
         onClipEdited(existing);
      } else {
         // Create a new automation clip — no modal, just add to timeline
         const clip = {
            id: clipKey,
            trackId: trackId,
            paramName: paramName,
            inputEl: isKnobObj ? null : inputEl,
            min: min,
            max: max,
            step: step,
            points: [
               { x: 0, y: currentVal },
               { x: 1, y: currentVal },
            ],
            lengthBeats: 4,
            startBeat: 0,
            // For knob-style automation, store the knobId for registry lookup
            isKnob: isKnobObj,
            knobId: isKnobObj ? (inputEl.knobId || null) : null,
            setValue: isKnobObj ? inputEl.setValue : null,
            getValue: isKnobObj ? inputEl.getValue : null,
            element: isKnobObj ? inputEl.element : null,
         };
         automationClips.set(clipKey, clip);
         onClipCreated(clip);
      }
   };
}

// ---- Get all automation clips (for saving/loading) ----
function getAllClips() {
   const result = [];
   for (const [key, clip] of automationClips) {
      result.push({
         trackId: clip.trackId,
         paramName: clip.paramName,
         points: clip.points,
         lengthBeats: clip.lengthBeats,
         startBeat: clip.startBeat,
         min: clip.min,
         max: clip.max,
         step: clip.step,
         isKnob: clip.isKnob || false,
         knobId: clip.knobId || null,
      });
   }
   return result;
}

// ---- Load automation clips ----
function loadClips(clips) {
   automationClips.clear();
   interpolatorCache.clear();
   for (const c of clips) {
      const key = c.trackId + ':' + c.paramName;
      automationClips.set(key, c);
   }
}

module.exports = {
   initAutomation,
   tickAutomations,
   setExternalAutoTracks,
   getAutomationValueAt,
   evalAutomation,
   onAutomationUpdate,
   offAutomationUpdate,
   getAllClips,
   loadClips,
   automationClips,
   invalidateInterpolator,
   sampleCurve,
};
